import { com, kotlin, Nullable } from '@eidu/web'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import postEntityType from './api/entity/type/postEntityType'
import patchEntityType from './api/entity/type/patchEntityType'
import deleteEntityType from './api/entity/type/deleteEntityType'
import { auditLogKeys, entityTypeKeys } from './queryKeys'
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import EntityLabel = com.eidu.entity.label.EntityLabel
import Field = com.eidu.entity.field.Field
import FieldId = com.eidu.entity.field.FieldId
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import PatchTypeRequest = com.eidu.entity.api.types.PatchTypeRequest
import PatchTypeResponse = com.eidu.entity.api.types.PatchTypeResponse
import KtList = kotlin.collections.KtList

type CreateParams = {
  name: string
  fieldsToCreate: readonly Field[]
  label: EntityLabel
  kind: EntityTypeKind
}

export const useCreateEntityType = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ name, fieldsToCreate, label, kind }: CreateParams): Promise<EntityType> =>
      postEntityType({ name, fieldsToCreate, label, kind }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityTypeKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}

type ModifyParams = {
  id: EntityTypeId
  entityTypeName: string | undefined
  label: EntityLabel | undefined
  fieldsToCreate: readonly Field[]
  fieldsToUpdate?: readonly Field[]
  fieldsToDelete?: readonly FieldId[]
  fieldOrder?: readonly FieldId[]
  fieldOrderChanged?: boolean
}

export const useModifyEntityType = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      entityTypeName,
      label,
      fieldsToCreate,
      fieldsToUpdate = [],
      fieldsToDelete = [],
      fieldOrder,
      fieldOrderChanged = false,
    }: ModifyParams): Promise<Nullable<PatchTypeResponse>> => {
      const mutatesFields = fieldsToCreate.length > 0 || fieldsToUpdate.length > 0 || fieldsToDelete.length > 0
      const includeFieldOrder = !!fieldOrder && (mutatesFields || fieldOrderChanged)
      const patchTypeRequest: PatchTypeRequest = new PatchTypeRequest(
        entityTypeName,
        KtList.fromJsArray(fieldsToCreate),
        KtList.fromJsArray(fieldsToUpdate),
        KtList.fromJsArray(fieldsToDelete),
        label,
        includeFieldOrder ? KtList.fromJsArray(fieldOrder) : null
      )
      const hasModifications =
        patchTypeRequest.name ||
        patchTypeRequest.label ||
        patchTypeRequest.fieldsToCreate.asJsReadonlyArrayView().length > 0 ||
        patchTypeRequest.fieldsToUpdate.asJsReadonlyArrayView().length > 0 ||
        patchTypeRequest.fieldsToDelete.asJsReadonlyArrayView().length > 0 ||
        includeFieldOrder
      if (!hasModifications) return null
      return patchEntityType({ id, patchTypeRequest })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityTypeKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}

export const useDeleteEntityType = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: EntityTypeId): Promise<void> => deleteEntityType({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityTypeKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}
